import React, { useState, useEffect } from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    BooleanInput,
    SelectInput,
    required,
    useNotify,
    useTranslate,
    useRedirect,
    FormDataConsumer,
    Toolbar,
    SaveButton,
} from 'react-admin';
import { Box, Typography, Button, LinearProgress, Alert, Card, CardContent, IconButton, ToggleButton, ToggleButtonGroup, TextField } from '@mui/material';
import { CloudUpload, CloudDownload, Memory, CheckCircle, ArrowBack, Link as LinkIcon } from '@mui/icons-material';
import { tokenManager } from '../../utils/tokenManager';
import { API_CONFIG } from '../../config/api';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const FIRMWARE_KNOWN_FIELDS = new Set([
    'file', 'file_url', 'vendor', 'model', 'version', 'description', 'is_active', 'release_notes',
]);

type FirmwareSource = 'upload' | 'url';

const mapFirmwareBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const firmwareFieldLabelKey = (field: string): string =>
    FIRMWARE_KNOWN_FIELDS.has(field)
        ? `resources.charger-firmware.fields.${field}`
        : field;

const FirmwareFileInput = ({ onChange }: { onChange: (file: File | null) => void }) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const translate = useTranslate();

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] || null;
        setSelectedFile(file);
        onChange(file);
    };

    return (
        <Box sx={{ my: 2 }}>
            <Button
                variant="contained"
                component="label"
                startIcon={<CloudUpload />}
                sx={{
                    backgroundColor: '#009A75',
                    '&:hover': {
                        backgroundColor: '#007F5F',
                    },
                }}
            >
                {translate('resources.charger-firmware.actions.upload')}
                <input
                    type="file"
                    hidden
                    accept=".bin,.hex,.fw,.rbl"
                    onChange={handleFileChange}
                />
            </Button>
            {selectedFile && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#E8F5E8', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#009A75' }}>
                        {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
                    </Typography>
                </Box>
            )}
            {!selectedFile && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    {translate('resources.charger-firmware.helpers.file_upload')}
                </Typography>
            )}
        </Box>
    );
};

const CreateToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: { xs: 2, sm: 4 },
            '& .MuiButton-root': {
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
            },
        }}
    >
        <SaveButton
            variant="contained"
            sx={{
                backgroundColor: '#00C853',
                '&:hover': {
                    backgroundColor: '#00A843',
                    boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                },
            }}
        />
    </Toolbar>
);

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/charger-firmware')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.charger-firmware.create.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.charger-firmware.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

const FirmwareCreate = () => {
    const [source, setSource] = useState<FirmwareSource>('upload');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [externalUrl, setExternalUrl] = useState<string>('');
    const [uploading, setUploading] = useState(false);
    const [vendors, setVendors] = useState<string[]>([]);
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
    const [modelsByVendor, setModelsByVendor] = useState<{ [key: string]: string[] }>({});
    const notify = useNotify();
    const translate = useTranslate();
    const redirect = useRedirect();

    // Fetch vendors and models from API
    useEffect(() => {
        const fetchVendorsAndModels = async () => {
            try {
                const token = await tokenManager.getAccessToken();
                if (!token) {
                    throw new Error('No authentication token available');
                }

                const response = await fetch(`${API_CONFIG.baseURL}/api/backend/charger-firmware/vendors-models`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || 'Failed to fetch vendors and models');
                }

                const data = await response.json();
                setVendors(data.vendors);
                setModelsByVendor(data.modelsByVendor);
            } catch (error) {
                console.error('Failed to fetch vendors and models:', error);
                notify('resources.charger-firmware.notifications.error.load_vendors_models', { type: 'error' });
            }
        };

        fetchVendorsAndModels();
    }, [notify]);

    // Since this form bypasses react-admin's dataProvider (it talks to
    // multipart/form-data directly), we can't rely on apiClient's error
    // normalization. Replicate the same unwrap logic used in
    // src/utils/apiClient.ts handleError so the shapes stay aligned.
    const applyBackendError = (apiError: any) => {
        const rawErrors =
            apiError?.errors ??
            apiError?.details?.errors ??
            apiError?.details?.details ??
            (Array.isArray(apiError?.details) ? apiError.details : undefined);

        const fieldErrors: Record<string, string> = {};
        const summaryLines: string[] = [];

        const pushError = (path: string, rawMsg: string) => {
            const translated = translateServerMessage(rawMsg, translate);
            const field = mapFirmwareBackendPath(path);
            fieldErrors[field] = translated;
            const label = translate(firmwareFieldLabelKey(field), { _: field });
            summaryLines.push(`• ${label}: ${translated}`);
        };

        if (Array.isArray(rawErrors)) {
            for (const item of rawErrors) {
                const path =
                    Array.isArray(item?.path) && item.path.length > 0 ? item.path.join('.') : '_';
                pushError(path, item?.message || 'Invalid value');
            }
        } else if (rawErrors && typeof rawErrors === 'object') {
            for (const [path, msgs] of Object.entries(rawErrors)) {
                const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                pushError(path, rawMsg);
            }
        }

        setServerErrors(fieldErrors);

        const headerKey = apiError?.error_code
            ? `errors.${apiError.error_code}`
            : 'validation.formHasErrors';
        const header = translate(headerKey, { _: apiError?.error || apiError?.message || 'Error' });

        if (summaryLines.length > 0) {
            notify(`${header}\n${summaryLines.join('\n')}`, {
                type: 'error',
                multiLine: true,
                autoHideDuration: 10000,
            });
        } else {
            notify(header, { type: 'error', autoHideDuration: 6000 });
        }
    };

    const handleSubmit = async (data: any) => {
        setServerErrors({});

        // Client-side preconditions: the file input and URL input live outside
        // react-admin's form state (local useState), so we surface their
        // validation here rather than letting the backend return the same error.
        if (source === 'upload' && !selectedFile) {
            const msg = translate('resources.charger-firmware.validation.file_required', {
                _: 'A firmware file is required',
            });
            setServerErrors({ file: msg });
            notify(msg, { type: 'error' });
            return;
        }
        if (source === 'url') {
            const trimmed = externalUrl.trim();
            if (trimmed === '' || !/^https?:\/\//i.test(trimmed)) {
                const msg = translate('resources.charger-firmware.validation.file_url_required', {
                    _: 'A valid http(s) URL is required',
                });
                setServerErrors({ file_url: msg });
                notify(msg, { type: 'error' });
                return;
            }
        }

        setUploading(true);

        try {
            const token = await tokenManager.getAccessToken();

            if (source === 'upload') {
                // Multipart upload to /upload endpoint. Append fields verbatim
                // — do not silently coerce undefined/empty user input here.
                const formData = new FormData();
                formData.append('file', selectedFile as File);
                if (data.version !== undefined && data.version !== null) {
                    formData.append('version', String(data.version));
                }
                if (typeof data.description === 'string' && data.description.trim() !== '') {
                    formData.append('description', data.description);
                }
                if (data.vendor !== undefined && data.vendor !== null) {
                    formData.append('vendor', String(data.vendor));
                }
                if (data.model !== undefined && data.model !== null) {
                    formData.append('model', String(data.model));
                }
                if (typeof data.is_active === 'boolean') {
                    formData.append('is_active', String(data.is_active));
                }

                const response = await fetch(`${API_CONFIG.baseURL}/api/backend/charger-firmware/upload`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                    body: formData,
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    applyBackendError(errorData);
                    return;
                }
            } else {
                // External URL — JSON POST to the resource collection endpoint.
                const payload = {
                    vendor: data.vendor,
                    model: data.model,
                    version: data.version,
                    file_url: externalUrl.trim(),
                    description: typeof data.description === 'string' && data.description.trim() !== '' ? data.description : undefined,
                    is_active: typeof data.is_active === 'boolean' ? data.is_active : true,
                };

                const response = await fetch(`${API_CONFIG.baseURL}/api/backend/charger-firmware`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    applyBackendError(errorData);
                    return;
                }
            }

            setServerErrors({});
            notify(translate('resources.charger-firmware.notifications.uploaded'), { type: 'success' });
            redirect('/charger-firmware');
        } catch (error: any) {
            console.error('Firmware create error:', error);
            notify(translate('resources.charger-firmware.notifications.error.upload'), { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Create
            title={false}
            component="div"
            redirect="list"
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                '& .RaCreate-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                onSubmit={handleSubmit}
                toolbar={<CreateToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    {/* Basic Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Memory sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.charger-firmware.sections.basic_info')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="version"
                                    label={translate('resources.charger-firmware.fields.version')}
                                    helperText={translate('resources.charger-firmware.helpers.version')}
                                    validate={required()}
                                    fullWidth
                                />

                                <TextInput
                                    source="description"
                                    label={translate('resources.charger-firmware.fields.description')}
                                    helperText={translate('resources.charger-firmware.helpers.description')}
                                    multiline
                                    rows={3}
                                    fullWidth
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Firmware Source Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <CloudDownload sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.charger-firmware.sections.firmware_source')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <ToggleButtonGroup
                                    value={source}
                                    exclusive
                                    onChange={(_, v) => { if (v) setSource(v as FirmwareSource); }}
                                    size="small"
                                    fullWidth
                                >
                                    <ToggleButton value="upload">
                                        <CloudUpload sx={{ mr: 1 }} fontSize="small" />
                                        {translate('resources.charger-firmware.source.upload')}
                                    </ToggleButton>
                                    <ToggleButton value="url">
                                        <LinkIcon sx={{ mr: 1 }} fontSize="small" />
                                        {translate('resources.charger-firmware.source.url')}
                                    </ToggleButton>
                                </ToggleButtonGroup>

                                {source === 'upload' ? (
                                    <>
                                        <FirmwareFileInput onChange={setSelectedFile} />
                                        <Alert severity="info">
                                            {translate('resources.charger-firmware.validation.invalid_file_type')}
                                        </Alert>
                                    </>
                                ) : (
                                    <>
                                        <TextField
                                            label={translate('resources.charger-firmware.fields.file_url')}
                                            helperText={serverErrors.file_url || translate('resources.charger-firmware.helpers.file_url')}
                                            value={externalUrl}
                                            onChange={(e) => setExternalUrl(e.target.value)}
                                            error={!!serverErrors.file_url}
                                            placeholder="http://example.com/firmware.bin"
                                            fullWidth
                                        />
                                        <Alert severity="warning">
                                            {translate('resources.charger-firmware.helpers.url_warning')}
                                        </Alert>
                                    </>
                                )}

                                {uploading && (
                                    <Box>
                                        <LinearProgress />
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                            {source === 'upload'
                                                ? translate('resources.charger-firmware.notifications.uploading')
                                                : translate('resources.charger-firmware.notifications.saving')}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Compatibility Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <CheckCircle sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.charger-firmware.sections.compatibility')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <SelectInput
                                    source="vendor"
                                    label={translate('resources.charger-firmware.fields.vendor')}
                                    helperText={translate('resources.charger-firmware.helpers.vendor')}
                                    choices={vendors.map(v => ({ id: v, name: v }))}
                                    validate={required()}
                                    fullWidth
                                />

                                <FormDataConsumer>
                                    {({ formData }) => {
                                        const availableModels = formData.vendor ? modelsByVendor[formData.vendor] || [] : [];
                                        return (
                                            <SelectInput
                                                source="model"
                                                label={translate('resources.charger-firmware.fields.model')}
                                                helperText={
                                                    formData.vendor
                                                        ? translate('resources.charger-firmware.helpers.model')
                                                        : translate('resources.charger-firmware.helpers.select_vendor_first')
                                                }
                                                choices={availableModels.map(m => ({ id: m, name: m }))}
                                                validate={required()}
                                                disabled={!formData.vendor || availableModels.length === 0}
                                                fullWidth
                                            />
                                        );
                                    }}
                                </FormDataConsumer>

                                <BooleanInput
                                    source="is_active"
                                    label={translate('resources.charger-firmware.fields.is_active')}
                                    defaultValue={true}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Create>
    );
};

export default FirmwareCreate;
